import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../utils/numberUtil';

interface WalletPanelProps {
  balance: number;
  vipLevel: number;
  creditScore: number;
  onWithdraw: () => void;
  /** True only until the first response lands; shows a shimmer in place of the figure. */
  pending?: boolean;
}

/**
 * The account page's focal point: balance, VIP level, and the withdraw action.
 *
 * The only dark surface on the page, so the eye lands here before anywhere
 * else. Everything decorative is layered into one absolutely-positioned stack
 * behind a `relative z-10` content column — the gradients, the sheen and the
 * top hairline therefore stay out of the readable markup, and none of them can
 * intercept a click on the button.
 *
 * All three values are data the page already fetches. The panel deliberately
 * makes no claim about whether a withdrawal will succeed: `withdrawEnable` is
 * only fetched by WithdrawalPage, so anything stated here would be a guess.
 */
const WalletPanel: React.FC<WalletPanelProps> = ({
  balance,
  vipLevel,
  creditScore,
  onWithdraw,
  pending = false,
}) => {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden rounded-lg bg-brand-900 shadow-[0_10px_30px_-12px_rgba(42,38,38,.45)]">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(145deg, #2A2626 0%, #413B3B 55%, #2A2626 100%)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(120% 100% at 85% -15%, rgba(200,149,47,.28), rgba(200,149,47,0) 60%)',
        }}
      />
      {/* Brushed-metal sheen. Deliberately near the threshold of visibility —
          at higher contrast it reads as a stripe pattern rather than a finish. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage:
            'repeating-linear-gradient(115deg, rgba(255,255,255,.05) 0 1px, rgba(255,255,255,0) 1px 7px)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgba(200,149,47,0) 0%, rgba(200,149,47,.9) 50%, rgba(200,149,47,0) 100%)',
        }}
      />

      <div className="relative z-10 p-6 lg:p-7">
        <div className="flex items-start justify-between gap-4">
          <span className="text-xs uppercase tracking-[0.18em] text-accent-500">
            {t('digital_balance')}
          </span>
          <span className="shrink-0 rounded-full border border-solid border-accent-500/50 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-accent-500">
            VIP {vipLevel}
          </span>
        </div>

        <div className="mt-4 flex items-baseline gap-2 text-white">
          <span className="text-sm tracking-[0.1em] text-white/70">RM</span>
          {pending ? (
            // A placeholder rather than RM 0.00. The page holds `loading` for a
            // further 1.5s after the response, so a real zero would sit on
            // screen long enough to be read as the user's actual balance.
            <span
              aria-hidden="true"
              className="h-9 w-44 animate-pulse rounded bg-white/10 lg:h-11"
            />
          ) : (
            // `tabular-nums` so the figure does not shift sideways between renders.
            <span className="font-display text-[38px] leading-none tabular-nums lg:text-display-lg">
              {formatNumber(balance)}
            </span>
          )}
        </div>

        <div className="mt-5 flex items-center">
          <span className="h-px w-16 bg-accent-500" />
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="mt-3 text-xs text-white/65">
          {t('credit_score')}: <span className="tabular-nums">{creditScore}</span>
        </div>

        <button
          type="button"
          onClick={onWithdraw}
          className={
            'group relative mt-6 flex w-full items-center justify-center gap-3 overflow-hidden ' +
            'cursor-pointer rounded-md border-0 bg-accent-500 px-8 py-3.5 ' +
            'text-xs font-semibold uppercase tracking-[0.2em] text-brand-900 ' +
            'transition-colors duration-200 hover:bg-accent-600 active:scale-[.99] ' +
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
            'focus-visible:outline-accent-500 lg:inline-flex lg:w-auto ' +
            // Light sweep on hover: a skewed highlight parked off the left edge,
            // slid across by a transform transition. No keyframes, no JS — and
            // hidden outright for anyone who has asked for reduced motion.
            "before:absolute before:inset-y-0 before:-left-full before:w-1/2 before:content-[''] " +
            'before:-skew-x-12 before:bg-gradient-to-r before:from-transparent ' +
            'before:via-white/45 before:to-transparent before:transition-transform ' +
            'before:duration-700 before:ease-out hover:before:translate-x-[300%] ' +
            'motion-reduce:before:hidden'
          }
        >
          <span className="relative z-10">{t('withdraw')}</span>
          <span aria-hidden="true" className="relative z-10 leading-none">
            &rarr;
          </span>
        </button>
      </div>
    </section>
  );
};

export default WalletPanel;
